import React, { useRef, useState, useEffect, useCallback } from 'react';
import { isEqual } from 'lodash';
import Article from './Article';
import './ColumnView.css';
import { useConfig } from '../../contexts/FrontendConfigContext';
import { clearSelectedStoryIndex, setSelectedStoryIndex, updateSelectedClaimsOrder } from './slice';
import { useDispatch, useSelector } from 'react-redux';

const ColumnView = ({
                        columnKey,
                        articles,
                        handleArticleSelectionChange,
                        handleClaimSelectionChange,
                        handleStorySelectionChange,
                        selectedArticles,
                        selectedClaims,
                        selectedStories,
                        formatDate,
                        handleAddUrl,
                        translate,
                        dragable,
                        hoverable,
                        handleMergeStories, // New prop for merge callback
                        animationKey,
                        currentStep,
                        alreadyDoneStoriesIds,
                        prs,
                        showToastNotification,
                        onLoadMore,         // Callback to fetch more stories from server
                        hasMore,            // Whether more stories are available on server
                        isLoadingMore,      // Whether a loadMore request is in flight
                    }) => {
    const { ARTICLE_PAGE_SIZE } = useConfig();
    const dispatch = useDispatch();
    const selectedStoryIndex = useSelector((state) => state.articles.selectedStoryIndex);
    const expandAllTrigger = useSelector((state) => state.articles.expandAllTrigger);

    const initialItemCount = ARTICLE_PAGE_SIZE;

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [manualLink, setManualLink] = useState('');
    const [visibleArticles, setVisibleArticles] = useState(() => {
        // Initialize from articles prop directly to avoid blank render on mount
        return (articles || []).slice(0, initialItemCount);
    });
    const [hasMoreArticles, setHasMoreArticles] = useState(() => {
        return (articles || []).length > initialItemCount;
    });
    const [isLoading, setIsLoading] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(null);
    const modalRef = useRef(null);
    const containerRef = useRef(null);
    const previousArticlesRef = useRef(articles);
    const [hoveredIndex, setHoveredIndex] = useState(-1);
    const [selectedForMerge, setSelectedForMerge] = useState([]);
    const articleRefs = useRef({});


    const loadInitialArticles = useCallback(async () => {
        try {
            const initialArticles = (articles || []).slice(0, ARTICLE_PAGE_SIZE);
            const translatedArticles = translate ? await translate(initialArticles) : initialArticles;
            setVisibleArticles(translatedArticles || initialArticles);
            setHasMoreArticles(initialArticles.length === ARTICLE_PAGE_SIZE);
            previousArticlesRef.current = articles;
        } catch (error) {
            console.error('Error loading articles:', error);
            // On error, show articles without translation
            const initialArticles = (articles || []).slice(0, ARTICLE_PAGE_SIZE);
            setVisibleArticles(initialArticles);
            setHasMoreArticles(initialArticles.length === ARTICLE_PAGE_SIZE);
            previousArticlesRef.current = articles;
        } finally {
            setIsLoading(false);
        }
    }, [articles, translate]);

    const loadMoreArticles = useCallback(async () => {
        const currentLength = visibleArticles.length;
        const moreArticles = articles.slice(currentLength, currentLength + ARTICLE_PAGE_SIZE);
        if (moreArticles.length > 0) {
            const translatedArticles = translate ? await translate(moreArticles) : moreArticles;
            setVisibleArticles((prev) => [...prev, ...translatedArticles]);
            setHasMoreArticles(translatedArticles.length === ARTICLE_PAGE_SIZE);
        } else {
            setHasMoreArticles(false);
        }
    }, [articles, translate, visibleArticles.length]);

    useEffect(() => {
        if (!isEqual(articles, previousArticlesRef.current)) {
            const prev = previousArticlesRef.current || [];
            const curr = articles || [];
            
            // Detect append: new articles start with the same items as before
            // This happens when infinite scroll loads the next page of stories
            const isAppend = prev.length > 0 &&
                curr.length > prev.length &&
                prev.every((item, i) => item._id === curr[i]?._id);
            
            if (isAppend) {
                // Append only the new items — preserve scroll position and existing visible items
                const newItems = curr.slice(prev.length);
                const translatedItems = newItems; // Translation happens in loadMoreArticles if needed
                setVisibleArticles(prevVisible => [...prevVisible, ...translatedItems]);
                setHasMoreArticles(curr.length - visibleArticles.length > ARTICLE_PAGE_SIZE);
                previousArticlesRef.current = articles;
            } else {
                // Full replacement (new search, different query) — reset everything
                setVisibleArticles([]);
                setIsLoading(true);
            }
        }
    }, [articles]);

    useEffect(() => {
        if (isLoading) {
            loadInitialArticles();
        }
    }, [isLoading, loadInitialArticles]);

    // Sentinel ref for IntersectionObserver-based infinite scroll
    const sentinelRef = useRef(null);

    // IntersectionObserver to detect when the sentinel at the bottom becomes visible
    // This replaces scroll event listeners and works regardless of which ancestor scrolls
    useEffect(() => {
        if (!sentinelRef.current) return;

        const observer = new IntersectionObserver(
            (entries) => {
                const entry = entries[0];
                if (entry.isIntersecting) {
                    if (hasMoreArticles) {
                        loadMoreArticles();
                    } else if (currentStep === 0 && hasMore && !isLoadingMore && onLoadMore) {
                        onLoadMore();
                    }
                }
            },
            { rootMargin: '200px' } // Trigger 200px before sentinel is visible
        );

        observer.observe(sentinelRef.current);
        return () => observer.disconnect();
    }, [hasMoreArticles, loadMoreArticles, hasMore, isLoadingMore, onLoadMore, currentStep]);

    const openModal = useCallback(() => {
        setIsModalOpen(true);
    }, []);

    const closeModal = useCallback(() => {
        setIsModalOpen(false);
        setManualLink('');
    }, []);

    const handleManualLinkChange = useCallback((event) => {
        setManualLink(event.target.value);
    }, []);

    const isValidUrl = (url) => {
        const pattern = new RegExp(
            '^(https?:\\/\\/)?' +
            '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' +
            '((\\d{1,3}\\.){3}\\d{1,3}))' +
            '(\\:\\d+)?(\\/[-a-z\\d%_.~+,]*)*' +
            '(\\?[;&a-z\\d%_.~+=-]*)?' +
            '(\\#[-a-z\\d_]*)?$',
            'i'
        );
        return !!pattern.test(url);
    };

    const handleManualLinkSubmit = useCallback(() => {
        if (isValidUrl(manualLink)) {
            handleAddUrl(manualLink, columnKey * 10);
            closeModal();
        } else {
            alert('Please enter a valid URL.');
        }
    }, [manualLink, handleAddUrl, columnKey, closeModal]);

    const isArticleSelected = useCallback(
        (articleUrl) => {
            const article = selectedArticles.find((item) => item.url === articleUrl);
            return article ? article.isSelected : false;
        },
        [selectedArticles]
    );

    const isClaimSelected = useCallback(
        (claimId) => {
            const claim = selectedClaims.find((item) => item._id === claimId);
            return claim ? claim.isSelected : false;
        },
        [selectedClaims]
    );

    const isStorySelected = useCallback(
        (storyId) => {
            const story = selectedStories.find((item) => item.groupSubject?._id === storyId);
            return story ? story.isSelected : false;
        },
        [selectedStories]
    );

    const toggleArticleSelection = useCallback(
        (article) => {
            handleArticleSelectionChange(article, !isArticleSelected(article.url));
        },
        [handleArticleSelectionChange, isArticleSelected]
    );

    const toggleClaimSelection = useCallback(
        (claim) => {
            handleClaimSelectionChange(claim, !isClaimSelected(claim._id));
        },
        [handleClaimSelectionChange, isClaimSelected]
    );

    const toggleStorySelection = useCallback(
        (story, index) => {
            handleStorySelectionChange(story, story.articles);
            dispatch(setSelectedStoryIndex(index));
        },
        [handleStorySelectionChange, dispatch]
    );

    const isSelected = useCallback(
        (object) => {
            return object.type === 'story'
                ? isStorySelected(object._id)
                : object.articleCount || object.type === 'claim'
                    ? isClaimSelected(object._id)
                    : isArticleSelected(object.url);
        },
        [isClaimSelected, isArticleSelected, isStorySelected]
    );

    function sortArrayByIdOrder(array1, array2) {
        const idOrderMap = new Map();
        array2.forEach((item, index) => {
            idOrderMap.set(item._id.toString(), index);
        });

        return [...array1].sort((a, b) => {
            const indexA = idOrderMap.get(a._id.toString());
            const indexB = idOrderMap.get(b._id.toString());

            if (indexA !== undefined && indexB !== undefined) {
                return indexA - indexB;
            }
            if (indexA !== undefined) {
                return -1;
            }
            if (indexB !== undefined) {
                return 1;
            }
            return 0;
        });
    }

    const moveArticle = (fromIndex, toIndex) => {
        const updatedArticles = Array.from(visibleArticles);
        const [movedArticle] = updatedArticles.splice(fromIndex, 1);
        updatedArticles.splice(toIndex, 0, movedArticle);
        setVisibleArticles(updatedArticles);
        const updatedSelectedClaims = sortArrayByIdOrder(selectedClaims, updatedArticles);
        dispatch(updateSelectedClaimsOrder(updatedSelectedClaims));
    };

    const getArticleKey = (article, index, seenUrls) => {
        let key = article.url || index;
        if (seenUrls.has(key)) {
            key = `${article.url}-${index}`;
        }
        seenUrls.add(key);
        return key;
    };

    const toggleMergeSelection = useCallback(
        (article) => {
            setSelectedForMerge((prev) => {
                if (prev.some((a) => a._id === article._id)) {
                    return prev.filter((a) => a._id !== article._id);
                }
                return [...prev, article];
            });
        },
        []
    );

    const handleMerge = useCallback(() => {
        if (selectedForMerge.length >= 2 && handleMergeStories) {
            handleMergeStories(selectedForMerge);
            setSelectedForMerge([]);
        }
    }, [selectedForMerge, handleMergeStories]);

    const seenUrls = new Set();

    return (
        <div className="articles-column" ref={containerRef}>
            <div className="c-articles-container">
                {selectedForMerge.length >= 2 && (
                    <button onClick={handleMerge} style={{
                        position: 'fixed',
                        bottom: '20px',
                        right: '20px',
                        padding: '10px 20px',
                        backgroundColor: '#007bff',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '5px',
                        cursor: 'pointer',
                        boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
                        zIndex: 1000,
                        transition: 'background-color 0.3s ease',
                    }}
                            className="merge-button">
                        Merge {selectedForMerge.length} Stories
                    </button>
                )}
                {visibleArticles.filter(article => !article.isHidden).map((article, index) => {
                    const articleKey = getArticleKey(article, index, seenUrls);



                    const toggleSelection = (article) => {
                        if (article?.type === 'story') {
                            toggleStorySelection(article, index);
                        } else if (article.articleCount || article?.type === 'claim') {
                            toggleClaimSelection(article);
                        } else {
                            toggleArticleSelection(article);
                        }
                    };

                    return (
                        <Article
                            ref={(el) => (articleRefs.current[index] = el)}
                            key={articleKey}
                            article={article}
                            index={index}
                            moveArticle={moveArticle}
                            isSelected={isSelected(article)}
                            toggleSelection={toggleSelection}
                            type={
                                article?.type === 'story'
                                    ? 'story'
                                    : article.articleCount
                                        ? 'claim'
                                        : 'article'
                            }
                            formatDate={formatDate}
                            isDraggable={dragable}
                            hoverable={hoverable}
                            isHovered={index === hoveredIndex || (hoveredIndex === -1 && index === 0)}
                            setHoveredIndex={setHoveredIndex}
                            isMergeSelected={selectedForMerge.some((a) => a._id === article._id)}
                            toggleMergeSelection={handleMergeStories ? toggleMergeSelection : undefined}
                            animationKey={animationKey}
                            alreadyDone = {alreadyDoneStoriesIds?.some(id => id === article._id)}
                            prs = {prs}
                            showToastNotification={showToastNotification}
                            expandAllTrigger={expandAllTrigger}
                            isHighlighted={highlightedIndex === index}
                        />
                    );
                })}
                {/* Sentinel element for IntersectionObserver infinite scroll */}
                {currentStep === 0 && (hasMore || hasMoreArticles) && (
                    <div ref={sentinelRef} style={{ height: '1px', width: '100%' }} />
                )}
                {currentStep === 0 && isLoadingMore && (
                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: '20px',
                        width: '100%'
                    }}>
                        <div style={{
                            border: '3px solid #f3f3f3',
                            borderTop: '3px solid #FF6B35',
                            borderRadius: '50%',
                            width: '32px',
                            height: '32px',
                            animation: 'spin 1s linear infinite'
                        }}></div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ColumnView;
